import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import { cmsStrings } from "../terminology";
import type { StatusTone } from "../lib/overview-ops";
import type { ResellerProfileSummary } from "../lib/api";
import {
  RESELLER_STATE_LABELS,
  relatedResellerLinks,
  resellerAdminLogoPreviewSrc,
  resellerAssignmentDisplay,
  resellerBrandingDisplay,
  resellerStateDisplay,
  type ResellerSortKey,
} from "../lib/reseller-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

/** Meaningful alt + non-breaking failed-image state (inventory/inspect/editor). */
export function ResellerLogoThumb({
  profileId,
  logoPath,
  hasStagingLogo = false,
  version,
  name,
  variant = "thumb",
}: {
  profileId: string;
  logoPath: string | null;
  hasStagingLogo?: boolean;
  version?: number;
  name: string;
  variant?: "thumb" | "preview";
}) {
  const src = resellerAdminLogoPreviewSrc({
    profileId,
    logoPath,
    hasStagingLogo,
    version,
  });
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (!src || failed) {
    return (
      <span
        className={
          variant === "preview"
            ? "reseller-logo-preview reseller-logo-preview--fallback sites-inventory__muted"
            : "sites-inventory__muted"
        }
        role="img"
        aria-label="لوگو در دسترس نیست"
        data-vt-reseller-logo-fallback={failed ? "error" : "missing"}
      >
        {failed ? "بارگذاری لوگو ناموفق — fallback محلی" : "بدون تصویر لوگو"}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={`لوگوی پروفایل نماینده ${name}`}
      className={variant === "preview" ? "reseller-logo-preview__img" : "reseller-logo-thumb"}
      data-vt-reseller-logo="true"
      onError={() => setFailed(true)}
    />
  );
}

type FilterBarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  stateFilter: string;
  onStateFilterChange: (value: string) => void;
  clientFilter: string;
  onClientFilterChange: (value: string) => void;
  sort: ResellerSortKey;
  onSortChange: (value: ResellerSortKey) => void;
  organizationLabel: string;
};

export function ResellerFilterBar({
  search,
  onSearchChange,
  stateFilter,
  onStateFilterChange,
  clientFilter,
  onClientFilterChange,
  sort,
  onSortChange,
  organizationLabel,
}: FilterBarProps) {
  const ui = cmsStrings.resellerUi;
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: ui.filterChipAll },
    { id: "has_logo", label: ui.filterHasLogo },
    { id: "staging_logo", label: ui.filterStagingLogo },
    { id: "fallback", label: ui.filterFallback },
    { id: "assigned", label: ui.filterAssigned },
    { id: "unassigned", label: ui.filterUnassigned },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar reseller-filter-bar announcements-filter-bar"
      aria-label={ui.filterAria}
    >
      <p className="sites-inventory__muted reseller-filter-bar__org">
        {ui.orgScopeLabel}:{" "}
        <strong>
          <LtrIsolate mono>{organizationLabel}</LtrIsolate>
        </strong>
      </p>
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips reseller-filter-bar__chips"
        role="group"
        aria-label={ui.filterLogoAria}
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`filter-chip${clientFilter === c.id ? " filter-chip--active" : ""}`}
            onClick={() => onClientFilterChange(c.id)}
            aria-pressed={clientFilter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="reseller-filter-bar__controls">
        <label className="announcements-filter-bar__field">
          <span>{ui.searchLabel}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={ui.searchPlaceholder}
            aria-label={ui.searchAria}
          />
        </label>
        <label className="announcements-filter-bar__field">
          <span>{ui.stateLabel}</span>
          <select
            value={stateFilter}
            onChange={(e) => onStateFilterChange(e.target.value)}
            aria-label={ui.stateAria}
          >
            <option value="">{ui.filterAll}</option>
            <option value="draft">{RESELLER_STATE_LABELS.draft}</option>
            <option value="published">{RESELLER_STATE_LABELS.published}</option>
            <option value="archived">{RESELLER_STATE_LABELS.archived}</option>
          </select>
        </label>
        <label className="announcements-filter-bar__field">
          <span>{ui.sortLabel}</span>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as ResellerSortKey)}
            aria-label={ui.sortAria}
          >
            <option value="name">{ui.sortName}</option>
            <option value="code">{ui.sortCode}</option>
            <option value="state">{ui.sortState}</option>
            <option value="updated">{ui.sortUpdated}</option>
            <option value="sites">{ui.sortSites}</option>
          </select>
        </label>
      </div>
    </FilterToolbar>
  );
}

export function ResellerInspectSummary({
  item,
  organizationLabel,
}: {
  item: ResellerProfileSummary;
  organizationLabel: string;
}) {
  const ui = cmsStrings.resellerUi;
  const state = resellerStateDisplay(item.state);
  const branding = resellerBrandingDisplay(item);
  const assignment = resellerAssignmentDisplay(item);
  const links = relatedResellerLinks();

  return (
    <section
      className="content-inspect content-inspect--reseller"
      aria-labelledby="reseller-inspect-title"
    >
      <header className="content-inspect__hero">
        <div className="content-inspect__hero-row">
          <div>
            <p className="content-inspect__kind">{ui.inspectKind}</p>
            <h2 id="reseller-inspect-title" className="content-inspect__name">
              {item.operationalName}
            </h2>
            <p className="content-inspect__id">
              کد <LtrIsolate mono>{item.code}</LtrIsolate>
              {" · نسخه "}
              <LtrIsolate mono>{item.version}</LtrIsolate>
            </p>
          </div>
          <div className="content-inspect__badges" aria-label={ui.stateAria}>
            <span className={`state-badge ${TONE_BADGE[state.tone]}`}>{state.label}</span>
            <span className={`state-badge ${TONE_BADGE[branding.tone]}`}>{branding.label}</span>
            <span className={`state-badge ${TONE_BADGE[assignment.tone]}`}>{assignment.label}</span>
          </div>
        </div>
        <div className="content-inspect__logo-row">
          <ResellerLogoThumb
            profileId={item.id}
            logoPath={item.logoPath}
            hasStagingLogo={item.hasStagingLogo}
            version={item.version}
            name={item.operationalName}
          />
        </div>
      </header>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionIdentity}</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>{ui.factOrg}</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {organizationLabel}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>{ui.factId}</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.id}
              </bdi>
            </dd>
          </div>
        </dl>
      </div>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionLifecycle}</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>{ui.factLifecycle}</dt>
            <dd>
              {state.label}{" "}
              (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.state}
              </bdi>
              )
            </dd>
          </div>
          <div>
            <dt>{ui.factPublished}</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {formatKabulDisplay(item.publishedAt)}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>{ui.factUpdated}</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {formatKabulDisplay(item.updatedAt)}
              </bdi>
            </dd>
          </div>
        </dl>
      </div>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionBranding}</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>{ui.factBranding}</dt>
            <dd>{branding.label}</dd>
          </div>
          {item.logoPath ? (
            <div>
              <dt>{ui.factLogoPath}</dt>
              <dd>
                <bdi dir="ltr" className="ltr-isolate latin-digits">
                  {item.logoPath}
                </bdi>
              </dd>
            </div>
          ) : null}
        </dl>
      </div>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionAssignment}</h3>
        <dl className="content-inspect__facts">
          <div>
            <dt>{ui.factAssignment}</dt>
            <dd>{assignment.label}</dd>
          </div>
        </dl>
      </div>

      <nav className="content-inspect__text-nav" aria-label={ui.inspectSectionRelated}>
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="content-inspect__text-link">
            {link.label}
          </Link>
        ))}
      </nav>
    </section>
  );
}

export function ResellerInventoryList({
  rows,
  selectedId,
  canManage,
  onSelect,
  onEdit,
}: {
  rows: ResellerProfileSummary[];
  selectedId: string | null;
  canManage: boolean;
  onSelect: (item: ResellerProfileSummary) => void;
  onEdit?: (item: ResellerProfileSummary) => void;
}) {
  const ui = cmsStrings.resellerUi;
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label={ui.tableAria}
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">{ui.colLogo}</th>
              <th scope="col">{ui.colName}</th>
              <th scope="col">{ui.colCode}</th>
              <th scope="col">{ui.colStatus}</th>
              <th scope="col">{ui.colBranding}</th>
              <th scope="col">{ui.colSites}</th>
              <th scope="col">{ui.colUpdated}</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const state = resellerStateDisplay(row.state);
              const branding = resellerBrandingDisplay(row);
              const assignment = resellerAssignmentDisplay(row);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <ResellerLogoThumb
                      profileId={row.id}
                      logoPath={row.logoPath}
                      hasStagingLogo={row.hasStagingLogo}
                      version={row.version}
                      name={row.operationalName}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row)}
                      aria-pressed={selected}
                    >
                      {row.operationalName}
                    </button>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.code}>
                      <LtrIsolate mono>{row.code}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[state.tone]}`}>{state.label}</span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[branding.tone]}`}>
                      {branding.label}
                    </span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[assignment.tone]}`}>
                      {assignment.label}
                    </span>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.updatedAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.updatedAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row)}
                      >
                        {ui.details}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label={ui.cardsAria}
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const state = resellerStateDisplay(row.state);
          const branding = resellerBrandingDisplay(row);
          const assignment = resellerAssignmentDisplay(row);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">{row.operationalName}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[state.tone]}`}>{state.label}</span>
                  <span className={`state-badge ${TONE_BADGE[branding.tone]}`}>
                    {branding.label}
                  </span>
                  <span className={`state-badge ${TONE_BADGE[assignment.tone]}`}>
                    {assignment.label}
                  </span>
                </span>
                <span className="sites-inventory__muted">
                  <LtrIsolate mono>{row.code}</LtrIsolate>
                </span>
                <ResellerLogoThumb
                  profileId={row.id}
                  logoPath={row.logoPath}
                  hasStagingLogo={row.hasStagingLogo}
                  version={row.version}
                  name={row.operationalName}
                />
              </button>
              {canManage && (
                <div className="sites-inventory-card__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => (onEdit ?? onSelect)(row)}
                  >
                    {row.state !== "archived" ? "ویرایش" : "مشاهده"}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

export { RESELLER_STATE_LABELS };
